import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const UserTotpSettings = ({ open, onClose, apiBase, isDark, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [step, setStep] = useState("status");
  const [setupData, setSetupData] = useState(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const inputClass = isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-gray-300";
  const mutedClass = isDark ? "text-slate-400" : "text-gray-500";

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/auth/totp?action=status`);
      setEnabled(!!res.data.totp_enabled);
      setStep("status");
      setSetupData(null);
      setConfirmCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authenticator durumu alınamadı");
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (open) loadStatus();
  }, [open, loadStatus]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${apiBase}/auth/totp`, { action: "setup" });
      setSetupData(res.data);
      setStep("setup");
      setConfirmCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kurulum başlatılamadı");
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async () => {
    if (confirmCode.replace(/\D/g, "").length !== 6) {
      toast.error("6 haneli kodu girin");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${apiBase}/auth/totp`, { action: "confirm", code: confirmCode });
      toast.success("Authenticator etkinleştirildi");
      setEnabled(true);
      onStatusChange?.(true);
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Doğrulama başarısız");
    } finally {
      setLoading(false);
    }
  };

  const disableTotp = async () => {
    if (!disablePassword || disableCode.replace(/\D/g, "").length !== 6) {
      toast.error("Şifre ve 6 haneli kod gerekli");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${apiBase}/auth/totp`, {
        action: "disable",
        sifre: disablePassword,
        code: disableCode,
      });
      toast.success("Authenticator devre dışı bırakıldı");
      setEnabled(false);
      onStatusChange?.(false);
      setDisablePassword("");
      setDisableCode("");
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Devre dışı bırakılamadı");
    } finally {
      setLoading(false);
    }
  };

  const qrUrl = setupData?.otpauth_uri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=12&data=${encodeURIComponent(setupData.otpauth_uri)}`
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className={`max-w-md ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
            <KeyRound className="w-5 h-5 text-violet-500" />
            Authenticator (2FA)
          </DialogTitle>
        </DialogHeader>

        {loading && step === "status" && !setupData ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
          </div>
        ) : step === "setup" && setupData ? (
          <div className="space-y-4">
            <p className={`text-sm ${mutedClass}`}>
              Google Authenticator veya Microsoft Authenticator ile QR kodu tarayın. Hesap: <strong className={isDark ? "text-slate-200" : "text-gray-800"}>{setupData.account}</strong>
            </p>
            {qrUrl && (
              <div className={`mx-auto w-fit rounded-xl border p-3 ${isDark ? "border-slate-700 bg-white" : "border-gray-200 bg-white"}`}>
                <img src={qrUrl} alt="Authenticator QR" width={220} height={220} className="rounded-lg" />
              </div>
            )}
            <div>
              <Label className={mutedClass}>Manuel anahtar</Label>
              <Input readOnly value={setupData.secret || ""} className={`mt-1 font-mono text-xs ${inputClass}`} />
            </div>
            <div>
              <Label className={mutedClass}>Uygulamadaki 6 haneli kod</Label>
              <Input
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                placeholder="000000"
                className={`mt-1 text-center text-lg tracking-[0.35em] font-mono ${inputClass}`}
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("status")} className={isDark ? "border-slate-700" : ""}>Geri</Button>
              <Button type="button" onClick={confirmSetup} disabled={loading} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Etkinleştir
              </Button>
            </div>
          </div>
        ) : enabled ? (
          <div className="space-y-4">
            <div className={`rounded-xl border p-4 flex items-start gap-3 ${isDark ? "border-emerald-500/30 bg-emerald-500/10" : "border-emerald-200 bg-emerald-50"}`}>
              <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className={`font-medium ${isDark ? "text-emerald-300" : "text-emerald-800"}`}>Authenticator etkin</p>
                <p className={`text-sm mt-1 ${mutedClass}`}>Girişte uygulamanızdaki kod istenecek.</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label className={mutedClass}>Şifreniz</Label>
                <Input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} className={`mt-1 ${inputClass}`} />
              </div>
              <div>
                <Label className={mutedClass}>Authenticator kodu</Label>
                <Input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  className={`mt-1 text-center font-mono ${inputClass}`}
                />
              </div>
              <Button type="button" variant="outline" onClick={disableTotp} disabled={loading} className={`w-full ${isDark ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : "border-rose-200 text-rose-700 hover:bg-rose-50"}`}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldOff className="w-4 h-4 mr-2" />}
                Authenticator&apos;ı kapat
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className={`text-sm ${mutedClass}`}>
              Google Authenticator veya Microsoft Authenticator ile hesabınıza ekstra güvenlik ekleyin. Girişte uygulamadaki 6 haneli kodu kullanırsınız.
            </p>
            <Button type="button" onClick={startSetup} disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700 text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Authenticator kur
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserTotpSettings;
